/**
 * ClientSearchField — Sélecteur client unifié (Party Model)
 * Recherche entreprises + particuliers via /api/tiers/search.
 * Utilisé dans : Locations.js, Ventes.js, Atelier.js
 *
 * Props :
 *   value       { client_id, client_type, client_nom, tiers_id }
 *   onChange    (value) => void
 *   onOpen      (value) => void  — clic sur le nom pour ouvrir la fiche
 *   placeholder string
 *   readOnly    bool
 *   compact     bool  — style condensé pour les panels avec grille dense (Ventes, Atelier)
 *   testId      string — data-testid sur l'input de recherche
 */

import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import * as api from '../api';

const BADGE = {
  personne:   { background: '#EDE9FE', color: '#7C3AED' },
  entreprise: { background: '#DBEAFE', color: '#1D4ED8' },
};

export function ClientSearchField({
  value,
  onChange,
  onOpen,
  placeholder = 'Rechercher un client…',
  readOnly = false,
  compact = false,
  testId = 'client-search-input',
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Fermer le dropdown en cliquant à l'extérieur
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Recherche différée via /api/tiers/search
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.searchTiers(q, 15);
        setResults(res.data || []);
        setOpen(true);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const select = (r) => {
    onChange({ client_id: r.entity_id, client_type: r.client_type, client_nom: r.nom_facturation, tiers_id: r.tiers_id });
    setQ('');
    setOpen(false);
    setResults([]);
  };

  const clear = () => {
    onChange({ client_id: null, client_type: null, client_nom: null, tiers_id: null });
    setQ('');
    setResults([]);
  };

  const isParticulier = value?.client_type === 'personne';

  // ── État : client sélectionné ──────────────────────────────────────────────
  if (value?.client_id) {
    if (compact) {
      return (
        <div className="flex items-center justify-between gap-1 rounded-lg border px-2"
          style={{ borderColor: 'var(--border)', background: 'var(--surface)', height: 30 }}>
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs px-1.5 rounded font-semibold flex-shrink-0"
              style={BADGE[isParticulier ? 'personne' : 'entreprise']}>
              {isParticulier ? 'Part.' : 'Ent.'}
            </span>
            <button type="button" onClick={() => onOpen?.(value)}
              className="font-semibold text-xs truncate hover:underline"
              style={{ color: 'var(--primary-solid)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
              {value.client_nom}
            </button>
          </div>
          {!readOnly && (
            <button type="button" onClick={clear}
              className="flex-shrink-0 text-xs rounded px-1 hover:bg-red-50"
              style={{ color: '#DC2626', border: '1px solid #FCA5A5', height: 18, lineHeight: '16px' }}>✕</button>
          )}
        </div>
      );
    }

    return (
      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border"
        style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
        <span className="text-xs px-1.5 py-0.5 rounded font-semibold flex-shrink-0"
          style={BADGE[isParticulier ? 'personne' : 'entreprise']}>
          {isParticulier ? 'Particulier' : 'Entreprise'}
        </span>
        <button type="button" onClick={() => onOpen?.(value)}
          className="text-left text-sm font-medium hover:underline whitespace-nowrap"
          style={{ color: 'var(--primary-solid)' }}>
          {value.client_nom}
        </button>
        {!readOnly && (
          <button onClick={clear} className="p-0.5 rounded hover:bg-red-50">
            <X size={13} style={{ color: '#EF4444' }} />
          </button>
        )}
      </div>
    );
  }

  // ── État : champ de recherche ──────────────────────────────────────────────
  return (
    <div className="relative flex-1 min-w-0" ref={ref}>
      {compact ? (
        <div className="flex items-center gap-1.5 w-full rounded-lg border px-2 focus-within:ring-1 focus-within:ring-blue-400"
          style={{ height: 30, borderColor: 'var(--border)', background: 'var(--surface)' }}>
          <Search size={11} style={{ color: '#9CA3AF', flexShrink: 0 }} />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onFocus={() => q.length >= 2 && setOpen(true)}
            disabled={readOnly}
            placeholder={placeholder}
            className="flex-1 text-xs bg-transparent focus:outline-none"
            style={{ color: 'var(--text)' }}
            data-testid={testId}
          />
          {q && <button onClick={() => setQ('')} tabIndex={-1}><X size={10} style={{ color: '#9CA3AF' }} /></button>}
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border"
          style={{ borderColor: 'var(--border)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onFocus={() => q.length >= 2 && setOpen(true)}
            placeholder={placeholder}
            className="flex-1 text-sm outline-none bg-transparent"
            style={{ color: 'var(--text)' }}
            data-testid={testId}
          />
          {q && <button onClick={() => setQ('')} tabIndex={-1}><X size={12} style={{ color: 'var(--text-muted)' }} /></button>}
        </div>
      )}

      {/* Dropdown résultats */}
      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border shadow-xl z-50 overflow-hidden"
          style={{ borderColor: 'var(--border)' }}>
          {results.map(r => (
            <button
              key={r.entity_id}
              type="button"
              onMouseDown={() => select(r)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 border-b"
              style={{ borderColor: 'var(--border-light)' }}>
              <span className="text-xs px-1.5 py-0.5 rounded font-semibold flex-shrink-0"
                style={BADGE[r.client_type === 'personne' ? 'personne' : 'entreprise']}>
                {r.client_type === 'personne' ? 'Particulier' : 'Entreprise'}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{r.nom_facturation}</p>
                {r.ville && <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{r.ville}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && q.length >= 2 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border shadow-xl z-50 px-3 py-2.5 text-xs"
          style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
          Aucun résultat pour « {q} »
        </div>
      )}
    </div>
  );
}
